import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeature } from '@optimizely/react-sdk';
import type { MultiSelectDropDownOption } from '@replai-platform/ui-components';
import { RootState } from '../../../store/rootReducer';
import { ProjectActions } from '../../../store/project';
import useGetCampaigns from '../../../api/hooks/campaigns/useGetCampaigns';
import { FilterActions } from '../../../store/filters';
import type { CampaignTypeFilterProps } from '../Filters/CampaignTypeFilter';
import {
  CAMPAIGN_TYPE_FILTER_ENABLED_PROJECT_IDS,
  CAMPAIGN_TYPE_FILTER_FEATURE_FLAG,
  DEFAULT_EXCLUDED_CAMPAIGN_TYPES,
  getCampaignType,
  getCampaignTypePattern,
  isCampaignTypeAllowed,
} from '../common/campaignTypes';

/**
 * Campaign name patterns (see `getCampaignTypePattern`) of the campaign types the user marked. A type is in at
 * most one of the lists; the types in neither are left alone. With at least one type to consider, only those
 * are shown; otherwise everything but the excluded types is.
 */
export type CampaignTypeSelection = {
  toConsider: string[];
  toExclude: string[];
};

export const EMPTY_CAMPAIGN_TYPE_SELECTION: CampaignTypeSelection = { toConsider: [], toExclude: [] };

/**
 * Options and handlers of the Campaign Type filter, agnostic of where the selection is stored so that both the
 * global filters and the reports form can use it.
 *
 * @param selection patterns currently applied
 * @param initialized whether a selection (the user's or the default one) was already applied. While `false`,
 * the hook applies the default exclusion as soon as the project campaigns are known.
 * @param onChange called with the new selection. `isDefault` is `true` for the default exclusion.
 */
export const useCampaignTypeFilterOptions = ({
  selection,
  initialized,
  onChange,
}: {
  selection: CampaignTypeSelection;
  initialized: boolean;
  onChange: (selection: CampaignTypeSelection, meta: { isDefault: boolean }) => void;
}): {
  options: MultiSelectDropDownOption[];
  isLoading: boolean;
  hasData: boolean;
  isEnabled: boolean;
  onOptionsChange: (options: MultiSelectDropDownOption[]) => void;
} => {
  const dispatch = useDispatch();
  const projectId = useSelector((state: RootState) => state.project.id);
  const { endDate, startDate } = useSelector((state: RootState) => state.filters);
  const [isFeatureEnabled] = useFeature(CAMPAIGN_TYPE_FILTER_FEATURE_FLAG);

  const isEnabled = !!isFeatureEnabled && CAMPAIGN_TYPE_FILTER_ENABLED_PROJECT_IDS.includes(projectId);

  const { isSuccess, data, isLoading } = useGetCampaigns(
    {
      projectIds: [projectId],
      metricsFilters: {
        countriesToConsider: ['ALL'],
        dateEndTimestamp: new Date(endDate).getTime(),
        dateStartTimestamp: new Date(startDate).getTime(),
      },
    },
    { enabled: isEnabled }
  );

  useEffect(() => {
    if (isSuccess && data && data?.campaigns.length > 0) {
      dispatch(ProjectActions.changeCampaigns(data));
    }
  }, [data, isSuccess, dispatch]);

  // Campaign types present in the project, in the order they are first seen, each with its name pattern
  const campaignTypes = useMemo(() => {
    if (!isEnabled || data?.campaigns == null) {
      return [];
    }

    const types = data.campaigns
      .map((campaign) => getCampaignType(campaign.name))
      .filter((type): type is string => type != null && isCampaignTypeAllowed(projectId, type));

    return [...new Set(types)].map((type) => ({ label: type, pattern: getCampaignTypePattern(type) }));
  }, [data, isEnabled, projectId]);

  // Default: exclude the default campaign types, provided the project has campaigns of those types
  useEffect(() => {
    if (!isEnabled || !isSuccess || initialized) {
      return;
    }

    const availablePatterns = campaignTypes.map((type) => type.pattern);
    const toExclude = DEFAULT_EXCLUDED_CAMPAIGN_TYPES.map(getCampaignTypePattern).filter((pattern) =>
      availablePatterns.includes(pattern)
    );

    onChange({ toConsider: [], toExclude }, { isDefault: true });
  }, [isEnabled, isSuccess, initialized, campaignTypes, onChange]);

  // The dropdown runs in 'include-exclude' mode: every option carries the mode the user gave it, if any
  const options = useMemo<MultiSelectDropDownOption[]>(
    () =>
      !isEnabled || !isSuccess
        ? []
        : campaignTypes.map((type) => {
            const mode = selection.toConsider.includes(type.pattern)
              ? 'include'
              : selection.toExclude.includes(type.pattern)
              ? 'exclude'
              : undefined;
            return { label: type.label, id: type.pattern, mode, selected: mode !== undefined };
          }),
    [campaignTypes, isEnabled, isSuccess, selection.toConsider, selection.toExclude]
  );

  const onOptionsChange = useCallback(
    (changedOptions: MultiSelectDropDownOption[]) => {
      const patternsWithMode = (mode: MultiSelectDropDownOption['mode']) =>
        changedOptions.filter((option) => option.mode === mode && option.id).map((option) => option.id as string);

      onChange(
        { toConsider: patternsWithMode('include'), toExclude: patternsWithMode('exclude') },
        { isDefault: false }
      );
    },
    [onChange]
  );

  return {
    options,
    isLoading: isEnabled && isLoading,
    hasData: options.length > 0,
    isEnabled,
    onOptionsChange,
  };
};

/**
 * Campaign Type filter bound to the global filters store - what the pages hand to the `FilterBar`.
 */
const useCampaignTypeFilter = (): CampaignTypeFilterProps => {
  const dispatch = useDispatch();
  const { campaignNamePatternsToConsider, campaignNamePatternsToExclude, campaignNamePatternsInitialized } =
    useSelector((state: RootState) => state.filters);

  const selection = useMemo<CampaignTypeSelection>(
    () =>
      campaignNamePatternsToConsider.length === 0 && campaignNamePatternsToExclude.length === 0
        ? EMPTY_CAMPAIGN_TYPE_SELECTION
        : { toConsider: campaignNamePatternsToConsider, toExclude: campaignNamePatternsToExclude },
    [campaignNamePatternsToConsider, campaignNamePatternsToExclude]
  );

  const onChange = useCallback(
    (newSelection: CampaignTypeSelection, { isDefault }: { isDefault: boolean }) => {
      dispatch(
        FilterActions.changeCampaignNamePatterns({
          ...newSelection,
          logEvent: !isDefault,
          logComponent: 'CampaignTypeFilter',
        })
      );
    },
    [dispatch]
  );

  const { options, isLoading, hasData, onOptionsChange } = useCampaignTypeFilterOptions({
    selection,
    initialized: campaignNamePatternsInitialized,
    onChange,
  });

  return useMemo<CampaignTypeFilterProps>(
    () => ({ options, loading: isLoading, hasData, onChange: onOptionsChange }),
    [options, isLoading, hasData, onOptionsChange]
  );
};

export default useCampaignTypeFilter;
